import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft,
  Mail,
  Phone,
  Globe,
  MapPin,
  Briefcase,
  Lock,
  User,
  Trash2,
  Plus,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function MultiStepRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') || 'base';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    tenantId: null,
    registrationToken: null,
    companyName: ''
  });

  // Step 1: Company Information
  const [companyData, setCompanyData] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    industry: '',
    size: '',
    location: '',
    plan_key: selectedPlan
  });

  // Step 2: Users
  const [users, setUsers] = useState([
    { email: '', password: '', first_name: '', last_name: '', role: 'admin' },
    { email: '', password: '', first_name: '', last_name: '', role: 'user' }
  ]);

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Manufacturing',
    'Education',
    'Real Estate',
    'Consulting',
    'Marketing',
    'Other'
  ];

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '500+'
  ];

  const planDetails = {
    base: { name: 'Base', price: '€29/month', users: 5, leads: 500 },
    tier1: { name: 'Tier 1', price: '€59/month', users: 25, leads: 2500 },
    tier2: { name: 'Tier 2', price: '€89/month', users: 50, leads: 2500 }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register/company`, companyData);
      
      setRegistrationData({
        tenantId: response.data.tenant_id,
        registrationToken: response.data.registration_token,
        companyName: response.data.company_name
      });
      
      toast.success('Company registered! Now add your users.');
      setCurrentStep(2);
    } catch (error) {
      console.error('Company registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUsersSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate at least 1 admin and 1 normal user
    const adminCount = users.filter(u => u.role === 'admin').length;
    const userCount = users.filter(u => u.role === 'user').length;

    if (adminCount < 1) {
      toast.error('At least 1 admin user is required');
      setLoading(false);
      return;
    }

    if (userCount < 1) {
      toast.error('At least 1 normal user is required');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register/users`, {
        tenant_id: registrationData.tenantId,
        registration_token: registrationData.registrationToken,
        users: users
      });
      
      toast.success('Registration complete! Check your email to verify your account.');
      setCurrentStep(3);
    } catch (error) {
      console.error('Users registration error:', error);
      toast.error(error.response?.data?.detail || 'User registration failed');
    } finally {
      setLoading(false);
    }
  };

  const addUser = (role) => {
    setUsers([...users, { email: '', password: '', first_name: '', last_name: '', role }]);
  };

  const removeUser = (index) => {
    if (users.length > 2) {
      setUsers(users.filter((_, i) => i !== index));
    } else {
      toast.error('Minimum 2 users required (1 admin + 1 normal user)');
    }
  };

  const updateUser = (index, field, value) => {
    const newUsers = [...users];
    newUsers[index][field] = value;
    setUsers(newUsers);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <div className="max-w-4xl mx-auto py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Create Your Account</h1>
          <p className="text-gray-600">
            Selected Plan: <span className="font-semibold text-blue-600">
              {planDetails[selectedPlan]?.name} ({planDetails[selectedPlan]?.price})
            </span>
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center space-x-4">
            {/* Step 1 */}
            <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                {currentStep > 1 ? <CheckCircle2 className="w-6 h-6" /> : <Building2 className="w-6 h-6" />}
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Company Info</span>
            </div>

            <ChevronRight className="w-6 h-6 text-gray-400" />

            {/* Step 2 */}
            <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                {currentStep > 2 ? <CheckCircle2 className="w-6 h-6" /> : <Users className="w-6 h-6" />}
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Add Users</span>
            </div>

            <ChevronRight className="w-6 h-6 text-gray-400" />

            {/* Step 3 */}
            <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Complete</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          
          {/* Step 1: Company Information */}
          {currentStep === 1 && (
            <form onSubmit={handleCompanySubmit} className="space-y-6">
              <div className="text-center mb-6">
                <Building2 className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
                <p className="text-gray-600 mt-2">Tell us about your company</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={companyData.company_name}
                    onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Acme Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={companyData.email}
                      onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="contact@acme.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={companyData.phone}
                      onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={companyData.website}
                      onChange={(e) => setCompanyData({...companyData, website: e.target.value})}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="www.acme.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={companyData.location}
                      onChange={(e) => setCompanyData({...companyData, location: e.target.value})}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="San Francisco, CA"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={companyData.industry}
                    onChange={(e) => setCompanyData({...companyData, industry: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Select industry</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size
                  </label>
                  <select
                    value={companyData.size}
                    onChange={(e) => setCompanyData({...companyData, size: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Select size</option>
                    {companySizes.map(size => (
                      <option key={size} value={size}>{size} employees</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={companyData.description}
                    onChange={(e) => setCompanyData({...companyData, description: e.target.value})}
                    rows={3}
                    maxLength={400}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Brief description of your company and what you do..."
                  />
                  <p className="text-sm text-gray-500 mt-1">{companyData.description.length}/400 characters</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Company...
                  </>
                ) : (
                  <>
                    Continue to Add Users
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: Users */}
          {currentStep === 2 && (
            <form onSubmit={handleUsersSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900">Add Your Team</h2>
                <p className="text-gray-600 mt-2">
                  Create user accounts for <strong>{registrationData.companyName}</strong>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Minimum: 1 Admin + 1 Normal User
                </p>
              </div>

              {/* Admin Users */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Admin Users</h3>
                  <button
                    type="button"
                    onClick={() => addUser('admin')}
                    className="text-blue-600 hover:text-blue-700 flex items-center text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Admin
                  </button>
                </div>
                
                {users.filter(u => u.role === 'admin').map((user, index) => {
                  const actualIndex = users.findIndex((u, i) => u.role === 'admin' && users.slice(0, i + 1).filter(x => x.role === 'admin').length === index + 1);
                  return (
                    <div key={actualIndex} className="bg-white p-4 rounded-lg mb-3 border border-blue-200">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                          <input
                            type="text"
                            required
                            value={user.first_name}
                            onChange={(e) => updateUser(actualIndex, 'first_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                          <input
                            type="text"
                            required
                            value={user.last_name}
                            onChange={(e) => updateUser(actualIndex, 'last_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input
                            type="email"
                            required
                            value={user.email}
                            onChange={(e) => updateUser(actualIndex, 'email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="john@company.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                          <input
                            type="password"
                            required
                            minLength={8}
                            value={user.password}
                            onChange={(e) => updateUser(actualIndex, 'password', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="Min. 8 characters"
                          />
                        </div>
                      </div>
                      {users.filter(u => u.role === 'admin').length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUser(actualIndex)}
                          className="mt-2 text-red-600 hover:text-red-700 flex items-center text-sm"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Normal Users */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Normal Users</h3>
                  <button
                    type="button"
                    onClick={() => addUser('user')}
                    className="text-blue-600 hover:text-blue-700 flex items-center text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add User
                  </button>
                </div>
                
                {users.filter(u => u.role === 'user').map((user, index) => {
                  const actualIndex = users.findIndex((u, i) => u.role === 'user' && users.slice(0, i + 1).filter(x => x.role === 'user').length === index + 1);
                  return (
                    <div key={actualIndex} className="bg-white p-4 rounded-lg mb-3 border border-gray-200">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                          <input
                            type="text"
                            required
                            value={user.first_name}
                            onChange={(e) => updateUser(actualIndex, 'first_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="Jane"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                          <input
                            type="text"
                            required
                            value={user.last_name}
                            onChange={(e) => updateUser(actualIndex, 'last_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="Smith"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input
                            type="email"
                            required
                            value={user.email}
                            onChange={(e) => updateUser(actualIndex, 'email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="jane@company.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                          <input
                            type="password"
                            required
                            minLength={8}
                            value={user.password}
                            onChange={(e) => updateUser(actualIndex, 'password', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                            placeholder="Min. 8 characters"
                          />
                        </div>
                      </div>
                      {users.filter(u => u.role === 'user').length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUser(actualIndex)}
                          className="mt-2 text-red-600 hover:text-red-700 flex items-center text-sm"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Accounts...
                    </>
                  ) : (
                    <>
                      Complete Registration
                      <CheckCircle2 className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Registration Complete!</h2>
              <p className="text-lg text-gray-600 mb-2">
                Your account for <strong>{registrationData.companyName}</strong> has been created.
              </p>
              <p className="text-gray-600 mb-8">
                We've sent verification emails to all users. Please check your inbox and verify your email address before logging in.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
                <h3 className="font-semibold text-gray-900 mb-3">Next Steps:</h3>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">1.</span>
                    <span>Check your email inbox for verification link</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">2.</span>
                    <span>Click the verification link to activate your account</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">3.</span>
                    <span>Return to the login page and sign in</span>
                  </li>
                </ol>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center"
              >
                Go to Login
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
